import React from "react";
import ReactTooltip from "react-tooltip";
import { ASSET_DASHBOARD_FROM, MOBILE_ASSET_DASHBOARD_FROM, resolveAssetDashboardFrom } from "../../assetDashboard/utils";
import store from "../../../store/configureStore";
import { buildThemeTokens, getThemeAccent, DEFAULT_DASHBOARD_THEME } from "../../../config/dashboardThemes";

export const FCA_DASHBOARD = "fcaDashboard";

export const DASHBOARD_MENU_PERMISSION = "dashboard";

export const DASHBOARD_OPTIONS = [
    {
        key: FCA_DASHBOARD,
        label: "FCA Dashboard",
        path: "/dashboard",
        chartPermission: "dashboard",
        icon: "fas fa-clipboard-list",
        theme: "fca-dash-theme",
        crumbKey: "main",
        crumbName: "Dashboard"
    },
    {
        key: ASSET_DASHBOARD_FROM,
        label: "Asset Dashboard",
        path: "/assetdashboard",
        chartPermission: "assets",
        source: ASSET_DASHBOARD_FROM,
        icon: "fas fa-cubes",
        theme: "asset-dash-theme",
        crumbKey: "assetDashboard",
        crumbName: "Asset Dashboard"
    },
    {
        key: MOBILE_ASSET_DASHBOARD_FROM,
        label: "Mobile Asset Dashboard",
        path: "/assetdashboard",
        chartPermission: "mobile_asset_dashboard",
        source: MOBILE_ASSET_DASHBOARD_FROM,
        icon: "fas fa-mobile-alt",
        theme: "mobile-asset-dash-theme",
        crumbKey: "assetDashboard",
        crumbName: "Mobile Assets"
    }
];

export const getDashboardPath = option =>
    option.source === MOBILE_ASSET_DASHBOARD_FROM ? `${option.path}?from=${MOBILE_ASSET_DASHBOARD_FROM}` : option.path;

export const getDashboardBreadCrumb = key => {
    const option = DASHBOARD_OPTIONS.find(dashboard => dashboard.key === key) || DASHBOARD_OPTIONS[0];
    return { key: option.crumbKey, name: option.crumbName, path: getDashboardPath(option), index: 0 };
};

export const DASHBOARD_PATHS = [...new Set(DASHBOARD_OPTIONS.map(option => option.path.toLowerCase()))];

export const DEFAULT_DASHBOARD_VIEW_BY_KEY = {
    [FCA_DASHBOARD]: "fca_dashboard",
    [ASSET_DASHBOARD_FROM]: "asset_dashboard",
    [MOBILE_ASSET_DASHBOARD_FROM]: "mobile_asset_dashboard"
};

const OPTION_ICON_GRADIENTS = DASHBOARD_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.key]: buildThemeTokens(getThemeAccent(option.theme))["--dash-icon-gradient"] }),
    {}
);

export const stripQuery = (path = "") => (path || "").split("?")[0].toLowerCase();

export const isWithinDashboardSection = (pathname = "", breadCrumbRoot = null) => {
    const path = stripQuery(pathname);
    if (DASHBOARD_PATHS.includes(path)) return true;
    const rootPath = stripQuery(breadCrumbRoot?.path);
    return Boolean(rootPath) && DASHBOARD_PATHS.includes(rootPath);
};

export const getAssetDashboardBreadCrumb = () =>
    getDashboardBreadCrumb(resolveAssetDashboardFrom(store.getState()?.assetManagementReducer?.chartParams));

export const getDashboardBreadCrumbForView = view =>
    getDashboardBreadCrumb(view === "mobile_asset_dashboard" ? MOBILE_ASSET_DASHBOARD_FROM : ASSET_DASHBOARD_FROM);

export const getDashboardTheme = key => DASHBOARD_OPTIONS.find(option => option.key === key)?.theme || DEFAULT_DASHBOARD_THEME;

export const SIDEBAR_CARD_TOOLTIP_ID = "sidebar-card-tip";
const FLYOUT_WIDTH = 250;
const FLYOUT_GAP = 8;

const DashboardOptionIcon = ({ option }) => <i className={option.icon} />;

class DashboardSwitcher extends React.Component {
    state = { open: false, flyoutPosition: null };

    componentDidMount() {
        document.addEventListener("mousedown", this.handleOutsideClick);
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.open !== this.state.open) ReactTooltip.rebuild();
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleOutsideClick);
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    setWrapperRef = node => {
        this.wrapperRef = node;
    };

    handleOutsideClick = event => {
        if (this.state.open && this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.close();
        }
    };

    handleKeyDown = event => {
        if (event.key === "Escape" && this.state.open) this.close();
    };

    close = () => this.setState({ open: false, flyoutPosition: null });

    getFlyoutPosition = () => {
        const card = this.wrapperRef?.querySelector(".dashboard-name-box")?.getBoundingClientRect();
        if (!card) return null;
        const sidebar = document.getElementById("sidebar")?.getBoundingClientRect();
        const left = (sidebar?.right ?? card.right) + FLYOUT_GAP;
        const maxTop = window.innerHeight - 20;
        return {
            top: Math.max(10, Math.min(card.top, maxTop)),
            left: Math.min(left, window.innerWidth - FLYOUT_WIDTH - FLYOUT_GAP)
        };
    };

    togglePicker = event => {
        event.stopPropagation();
        ReactTooltip.hide();
        if (this.state.open) {
            this.close();
            return;
        }
        this.setState({ open: true, flyoutPosition: this.getFlyoutPosition() });
    };

    handleCardClick = event => {
        const { options, activeKey, onSelect, isActiveRoute } = this.props;
        if (isActiveRoute && options.length > 1) {
            this.togglePicker(event);
            return;
        }
        const active = options.find(option => option.key === activeKey);
        if (active) onSelect(active);
    };

    handleSelect = option => {
        this.close();
        this.props.onSelect(option);
    };

    render() {
        const { options, activeKey, isActiveRoute } = this.props;
        const { open, flyoutPosition } = this.state;
        const active = options.find(option => option.key === activeKey) || options[0];
        if (!active) return null;
        const canSwitch = options.length > 1;
        const opensSwitcher = canSwitch && isActiveRoute;

        return (
            <div className="dashboard-switcher" ref={this.setWrapperRef}>
                <div
                    className={`dashboard-name-box project-name-wrapper d-flex ml-2 mr-2 ${isActiveRoute ? "is-active" : ""}`}
                    role="button"
                    tabIndex={0}
                    {...(opensSwitcher ? { "aria-haspopup": "listbox", "aria-expanded": open } : {})}
                    data-tip={open ? null : opensSwitcher ? "Click to switch dashboard" : "Click to go to this dashboard"}
                    data-for={SIDEBAR_CARD_TOOLTIP_ID}
                    onClick={this.handleCardClick}
                    onKeyDown={event => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            this.handleCardClick(event);
                        }
                    }}
                >
                    <span className="icon-circle">
                        <i className="fas fa-home" />
                    </span>
                    <div className="cont-rt">
                        <div className="project-label">Dashboard</div>
                        <div className="project-value">
                            <span>{active.label}</span>
                        </div>
                    </div>
                    {canSwitch && (
                        <span
                            className={`default-project-edit-icon dashboard-switcher-edit ${opensSwitcher ? "is-plain" : ""}`}
                            {...(opensSwitcher
                                ? { "aria-hidden": true }
                                : {
                                      role: "button",
                                      tabIndex: 0,
                                      "aria-haspopup": "listbox",
                                      "aria-expanded": open,
                                      "data-tip": open ? null : "Click to switch dashboard",
                                      "data-for": SIDEBAR_CARD_TOOLTIP_ID,
                                      onClick: this.togglePicker,
                                      onKeyDown: event => {
                                          if (event.key === "Enter" || event.key === " ") {
                                              event.preventDefault();
                                              this.togglePicker(event);
                                          }
                                      }
                                  })}
                        >
                            <i className="fas fa-exchange-alt fa-rotate-90" />
                        </span>
                    )}
                </div>
                {canSwitch && open && flyoutPosition && (
                    <div className="dashboard-switcher-flyout" style={{ top: flyoutPosition.top, left: flyoutPosition.left }} role="listbox">
                        <div className="dashboard-switcher-flyout-title">Switch Dashboard</div>
                        {options.map(option => (
                            <div
                                key={option.key}
                                className={`dashboard-switcher-option ${option.key === activeKey ? "selected" : ""}`}
                                role="option"
                                aria-selected={option.key === activeKey}
                                tabIndex={0}
                                onClick={() => this.handleSelect(option)}
                                onKeyDown={event => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        this.handleSelect(option);
                                    }
                                }}
                            >
                                <span
                                    className="dashboard-switcher-option-icon"
                                    style={{ background: OPTION_ICON_GRADIENTS[option.key] }}
                                >
                                    <DashboardOptionIcon option={option} />
                                </span>
                                <span className="dashboard-switcher-option-label">{option.label}</span>
                                {option.key === activeKey && <i className="fas fa-check" />}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default DashboardSwitcher;
